import { type FC } from 'react';
import cn from 'classnames';

import { toShortNumFormat } from '@/common/lib/toShortNumFormat';
import {
  type BalanceItem,
  type FilteredHoldedBalanceItem,
} from '@/common/types/user';
import { StringSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';

import cashPng from '/img/icons/cash-large.png';
import cashWebp from '/img/icons/cash-large.webp';
import dicePng from '/img/icons/dice.png';
import diceWebp from '/img/icons/dice.webp';
import usdPng from '/img/icons/usd-large.png';
import usdWebp from '/img/icons/usd-large.webp';
import tgemPng from '/img/tgem-big.png';
import tgemWebp from '/img/tgem-big.webp';

interface BalanceItemProps extends FilteredHoldedBalanceItem {
  className?: string;
}

const BalanceItem: FC<BalanceItemProps> = ({ name, value, className }) => {
  const labels: Partial<Record<BalanceItem, string>> = {
    usdt: 'USD',
    cash: 'Cash',
    dice: 'Dices',
    tgem: 'T-Gems',
  };

  const images: Partial<Record<BalanceItem, { png: string; webp: string }>> = {
    usdt: {
      png: usdPng,
      webp: usdWebp,
    },
    cash: {
      png: cashPng,
      webp: cashWebp,
    },
    dice: {
      png: dicePng,
      webp: diceWebp,
    },
    tgem: {
      png: tgemPng,
      webp: tgemWebp,
    },
  };

  const classNames = {
    ton: 'aspect-square',
    cash: 'aspect-square w-[3.6rem]',
    dice: 'aspect-square w-[3.5rem]',
  };

  const label = labels[name];
  const image = images[name];

  return (
    label && (
      <div
        className={cn(
          'flex flex-col items-center justify-center flex-1 h-[9.125rem]',
          className,
        )}
      >
        <div className="flex items-center justify-center aspect-square w-[3rem]">
          <Picture
            className={
              name in classNames
                ? classNames[name as keyof typeof classNames]
                : ''
            }
            img={image?.png}
            webp={image?.webp}
            alt={label}
          />
        </div>
        <span className="font-bold text-2xl leading-none mb-1">
          {toShortNumFormat(value)}
        </span>
        <StringSmall weight="medium" className="!text-white/40">
          {label}
        </StringSmall>
      </div>
    )
  );
};

export { BalanceItem };
